import React, { FunctionComponent } from 'react'
import { useT } from '@transifex/react'
import { Link } from '@lightspeed/design-system-react'
import { useAppDispatch, useAppSelector } from '../../../../../state/hooks'
import {
  openFulfilmentLineItemPopover,
  selectLineItemQuantityInputError,
} from '../../../../../state/slices/uiStateSlice'
import {
  useSaleInventoryData,
  useUserManager,
  useVdFeatureManager,
} from '../../../../../ng-compat/hooks'
import { ERRORS } from '../../../../../constants/sale-form-errors'
import {
  isNonFulfillmentOnAccountSale,
  isUnfulfilledLineItem,
} from '../../../../../functions/sale'
import {
  selectFulfilmentDetails,
  selectHasCompletedFulfilments,
  selectSaleStatus,
} from '../../../../../state/slices/currentSale/currentSaleSlice'
import { LineItem } from '../../../../../models/line-item'
import { PERMISSIONS } from '../../../../../constants'
import { FEATURE_VALIDATE_RETURN_QUANTITIES } from '../../../../../constants/wr-features'
import { selectSaleReturnFor } from '../../../../../state/selectors'
import { useSaleHasServiceOrder } from '../../../useSaleHasServiceOrder'
import { useSaleIsContinuedServiceSale } from '../../../useSaleIsContinuedServiceSale'
import { ContinueAnywayMessage } from '../ContinueAnywayMessage'
import { InputErrorMessage } from '../InputErrorMessage'

interface Props {
  lineItem: LineItem
}

export const LineItemQuantityErrorLegacy: FunctionComponent<Props> = ({
  lineItem,
}) => {
  const t = useT()
  const dispatch = useAppDispatch()
  const saleStatus = useAppSelector(selectSaleStatus)
  const fulfillmentDetails = useAppSelector(selectFulfilmentDetails)
  const hasCompletedFulfilments = useAppSelector(selectHasCompletedFulfilments)
  const isReferencedReturn = !!useAppSelector(selectSaleReturnFor)

  const saleInventoryData = useSaleInventoryData()
  const userManager = useUserManager()
  const vdFeatureManager = useVdFeatureManager()
  const isValidateReturnEnabled = vdFeatureManager.get(
    FEATURE_VALIDATE_RETURN_QUANTITIES
  )
  const saleHasServiceOrder = useSaleHasServiceOrder()
  const isContinuedServiceSale = useSaleIsContinuedServiceSale()

  const lineItemQuantityError = useAppSelector(
    selectLineItemQuantityInputError(lineItem.localId)
  )

  const handleContinueAnyway = () => {
    saleInventoryData.disableInventoryValidation(lineItem)
  }

  if (lineItemQuantityError === ERRORS.NO_INVENTORY) {
    const inventoryLevel =
      saleInventoryData.getInventoryData(lineItem)?.inventoryLevel ?? 0

    // For continued service sales, show "Continue anyway" link
    // For new service sales, don't show NO_INVENTORY errors - JAWS handles fulfillment
    if (saleHasServiceOrder) {
      if (isContinuedServiceSale) {
        return (
          <ContinueAnywayMessage
            inventoryLevel={inventoryLevel}
            onContinue={handleContinueAnyway}
            trackingLabel="continue-anyway-service-sale"
          />
        )
      }

      // For new service sales, hide NO_INVENTORY errors - JAWS handles fulfillment
      return null
    }

    if (
      isNonFulfillmentOnAccountSale({
        status: saleStatus,
        fulfilmentDetails: fulfillmentDetails,
      }) ||
      hasCompletedFulfilments ||
      isReferencedReturn
    ) {
      return (
        <ContinueAnywayMessage
          inventoryLevel={inventoryLevel}
          onContinue={handleContinueAnyway}
          trackingLabel="ignore-low-stock-warning"
        />
      )
    }

    return (
      <div data-cy="low-stock-warning">
        <InputErrorMessage
          errorElement={
            <>
              {t('There’s {inventoryLevel} available. ', {
                inventoryLevel,
              })}
              <Link
                href="#"
                onClick={() => {
                  dispatch(openFulfilmentLineItemPopover(lineItem.localId))
                }}
                data-cy="ignore-low-stock-warning"
                isSecondary={true}
                data-track="what-do-you-want-to-do"
              >
                {t('What do you want to do?')}
              </Link>
            </>
          }
        />
      </div>
    )
  }

  // Non inventory errors
  let errorMessage: string | null = null

  if (lineItemQuantityError === ERRORS.INVALID_MAX) {
    errorMessage = t('Quantity can’t be greater than 10 digits')
  }

  if (
    !lineItem.confirmed &&
    lineItemQuantityError === ERRORS.INVALID_MIN &&
    isUnfulfilledLineItem(lineItem)
  ) {
    errorMessage = t('Quantity can’t be negative for unfulfilled items')
  }

  // Return errors
  if (
    userManager.hasPermission(PERMISSIONS.SALE_RETURN) &&
    !isUnfulfilledLineItem(lineItem)
  ) {
    if (lineItemQuantityError === ERRORS.INVALID_MIN_RETURN_QUANTITY) {
      errorMessage = isValidateReturnEnabled
        ? t("Return quantity can't exceed quantity sold")
        : t('Return quantity can’t be more than original quantity sold')
    }
    if (lineItemQuantityError === ERRORS.INVALID_MAX_RETURN_QUANTITY) {
      errorMessage = t('Return quantity can’t be more than 0')
    }
  }

  if (lineItemQuantityError === ERRORS.INVALID_UNREFERENCED_RETURN) {
    errorMessage = t(
      "Quantity can't be negative for items not in the original sale"
    )
  }

  if (lineItemQuantityError === ERRORS.INVALID_CHARACTERS) {
    errorMessage = t('Enter a numeric value', {
      _context: 'Error message when the input contains non-numeric characters',
    })
  }

  return errorMessage ? (
    <InputErrorMessage errorElement={<>{errorMessage}</>} />
  ) : null
}
